import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { listGeosites } from '../api/geosites'
import type { GeositeResponse } from '../api/types'

const LS_KEY = 'box_geosites'
const QUERY_KEY = ['geosites'] as const

function readCache(): GeositeResponse[] | undefined {
  try {
    const raw = localStorage.getItem(LS_KEY)
    if (!raw) return undefined
    return JSON.parse(raw) as GeositeResponse[]
  } catch {
    return undefined
  }
}

function writeCache(data: GeositeResponse[]) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(data))
  } catch {
    // quota exceeded — ignore
  }
}

/**
 * Prefetch geosites on app start. Call once in a top-level component.
 */
export function usePrefetchGeosites() {
  const queryClient = useQueryClient()

  useEffect(() => {
    const cached = readCache()
    if (cached) {
      queryClient.setQueryData<GeositeResponse[]>(QUERY_KEY, cached)
    }
    void queryClient.prefetchQuery({
      queryKey: QUERY_KEY,
      queryFn: () => listGeosites(),
    })
  }, [queryClient])
}

/**
 * Access geosites with localStorage-backed cache.
 * Shows cached data instantly, refreshes in background.
 */
export function useGeosites() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => listGeosites(),
    initialData: readCache,
    staleTime: 5 * 60_000, // consider fresh for 5 min
    gcTime: Infinity,
    select: (data) => {
      writeCache(data)
      return data
    },
  })
}
